/**
 * Authentication context provider
 *
 * Provides auth state and methods to the component tree.
 * Uses TanStack Query session hook (`use-session.ts`) internally so that
 * cached session data survives transient API/network outages ("auth
 * outage grace mode").
 *
 * ## Auth modes
 *
 * - `"live"` -- the session was confirmed by the server.
 * - `"degraded"` -- the server is unreachable but we have a cached
 *   session or a localStorage-persisted last-known identity from a
 *   previous successful check.  The user stays inside the authenticated
 *   app (read-only) instead of being kicked to /login.
 *
 * ## Last-known identity persistence
 *
 * When a live session is successfully fetched, a minimal identity
 * snapshot (id, name, optional image, savedAt timestamp) is persisted
 * to localStorage under `floline:lastKnownIdentity`.
 * During network/server outages -- when neither the live session nor
 * the TanStack Query cache contain a user -- this snapshot is used as
 * a UI-only fallback so the header chip, etc. still show the user's
 * name/avatar.
 *
 * **Privacy: no email or other avoidable PII is stored.**  Only the
 * opaque user ID, display name, and avatar URL are persisted.
 *
 * **This is NOT proof of authentication.**  It is cleared on explicit
 * sign-out, on confirmed 401, and after a 24-hour TTL.
 *
 * Email-auth helpers (magic-link + OTP) follow an anti-enumeration pattern: they always
 * resolve successfully from the caller's perspective.  The UI should
 * show a generic "Check your email" message regardless of the outcome.
 */
import { createContext, useContext, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";
import { useSession, SESSION_QUERY_KEY } from "@/hooks/use-session";
import { API_URL, AUTH_MICROSOFT_ENABLED } from "@/env";
import { setTelemetryUserId, clearTelemetryUserId } from "@/telemetry";

// ---------------------------------------------------------------------------
// Last-known identity -- localStorage persistence
// ---------------------------------------------------------------------------

/** localStorage key for the last-known user identity snapshot. */
const LAST_KNOWN_IDENTITY_KEY = "floline:lastKnownIdentity";

/** Maximum age (ms) before a stored identity is considered stale and discarded. */
const LAST_KNOWN_IDENTITY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Minimal user identity snapshot persisted to localStorage.
 *
 * Privacy: email is intentionally excluded to minimize browser-stored PII.
 * Only the opaque user ID, display name, and optional avatar URL are stored.
 */
interface LastKnownIdentity {
  id: string;
  name: string;
  image?: string | null;
  /** ISO-8601 timestamp of when this snapshot was saved. */
  savedAt: string;
}

/**
 * Read the last-known identity from localStorage.
 *
 * Returns `null` if nothing is stored, the stored value is unparseable,
 * or the TTL has expired.
 *
 * Backward compatible: accepts old format that included `email` and
 * silently discards that field.
 */
function getLastKnownIdentity(): LastKnownIdentity | null {
  try {
    const raw = localStorage.getItem(LAST_KNOWN_IDENTITY_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as LastKnownIdentity).id !== "string" ||
      typeof (parsed as LastKnownIdentity).name !== "string" ||
      typeof (parsed as LastKnownIdentity).savedAt !== "string"
    ) {
      // Corrupt data -- discard silently.
      localStorage.removeItem(LAST_KNOWN_IDENTITY_KEY);
      return null;
    }

    const obj = parsed as Record<string, unknown>;
    const identity: LastKnownIdentity = {
      id: obj.id as string,
      name: (obj.name as string) || "Account",
      image: typeof obj.image === "string" ? obj.image : null,
      savedAt: obj.savedAt as string,
    };
    // Note: any `email` field from old format is intentionally discarded.

    const age = Date.now() - new Date(identity.savedAt).getTime();
    if (age > LAST_KNOWN_IDENTITY_TTL_MS) {
      // Expired -- discard.
      localStorage.removeItem(LAST_KNOWN_IDENTITY_KEY);
      return null;
    }

    return identity;
  } catch {
    // localStorage unavailable or JSON.parse failed -- safe to ignore.
    return null;
  }
}

/**
 * Persist a minimal identity snapshot to localStorage.
 *
 * Only the opaque user ID, display name, and optional avatar URL are
 * stored.  No email, tokens, session IDs, or other auth material.
 */
function saveLastKnownIdentity(user: {
  id: string;
  name: string | null;
  image?: string | null;
}): void {
  try {
    const identity: LastKnownIdentity = {
      id: user.id,
      name: user.name ?? "Account",
      image: user.image ?? null,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(LAST_KNOWN_IDENTITY_KEY, JSON.stringify(identity));
  } catch {
    // localStorage unavailable -- non-critical, ignore.
  }
}

/**
 * Remove the last-known identity from localStorage.
 * Called on explicit sign-out and confirmed 401 (unauthenticated).
 */
function clearLastKnownIdentity(): void {
  try {
    localStorage.removeItem(LAST_KNOWN_IDENTITY_KEY);
  } catch {
    // localStorage unavailable -- non-critical, ignore.
  }
}

// ---------------------------------------------------------------------------
// Result types for email-auth / invite flows
// ---------------------------------------------------------------------------

  /** Outcome of an invite-claim or email-auth request. */
export interface AuthFlowResult {
  ok: boolean;
  /** Machine-readable error code when ok is false (e.g. "INVALID_INVITE_CODE"). */
  error?: string;
}

/**
 * Auth context value type
 */
interface AuthContextValue {
  /** Current user or null if not authenticated */
  user: { id: string; name: string | null; email: string; image?: string | null } | null;
  /** Whether user is authenticated (possibly from cached session during outage) */
  isAuthenticated: boolean;
  /** Whether auth state is loading */
  isLoading: boolean;
  /**
   * Current auth connectivity mode:
   * - `"live"` -- session confirmed by the server on the most recent check.
   * - `"degraded"` -- server unreachable but cached session exists; user
   *   stays authenticated in a read-only/degraded experience.
   */
  authMode: "live" | "degraded";
  /**
   * Error from the last session fetch attempt, if any.
   * Non-null when `authMode === "degraded"` or when the session fetch
   * failed for any reason.
   */
  authError: Error | null;
  /** Sign in with Microsoft */
  signInWithMicrosoft: () => Promise<void>;
  /** Sign out current user */
  handleSignOut: () => Promise<void>;

  // ---- Email auth helpers ------------------------------------------------

  /**
   * Send an email OTP for sign-in.
   *
   * Always resolves with `{ ok: true }` to preserve anti-enumeration behavior.
   */
  sendEmailOtpSignIn: (email: string) => Promise<AuthFlowResult>;

  /**
   * Complete sign-in with an email OTP.
   *
   * Returns `{ ok: false, error }` for invalid/expired OTP so UI can render
   * actionable feedback.
   */
  signInWithEmailOtp: (email: string, otp: string) => Promise<AuthFlowResult>;

  /**
   * Claim an invite for a new email address.
   *
   * Calls `POST /api/invites/claim` with the email + invite code.
   * Returns `{ ok: false, error }` when the invite code is invalid
   * so the UI can display a meaningful message.
   */
  claimInvite: (email: string, inviteCode: string) => Promise<AuthFlowResult>;

  /**
    * Two-step helper for new-user registration:
    * 1. Claim the invite (validates code server-side).
    * 2. Send an OTP sign-in email.
   *
   * If the invite code is invalid, returns `{ ok: false, error }` and
   * does NOT proceed to step 2. Email enumeration is still protected:
   * the backend only reveals invite-code validity, not email existence.
   */
  sendMagicLinkRegister: (email: string, inviteCode: string) => Promise<AuthFlowResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth provider component
 *
 * Wraps the application with authentication context.
 * Uses TanStack Query useSession hook internally so that cached
 * session data survives transient API/network outages.
 *
 * Additionally persists a minimal last-known identity to localStorage
 * so the UI can still render user info (header chip, etc.) during
 * cold-start network outages where no TanStack Query cache exists.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const {
    session,
    isLoading,
    isNetworkError,
    error: sessionError,
  } = useSession();

  // ---------- Last-known identity persistence ----------------------------
  // Persist identity to localStorage when we get a live session.
  // Clear it when the user is confirmed unauthenticated (no error, no session).

  useEffect(() => {
    // Still loading -- don't act yet.
    if (isLoading) return;

    if (session?.user) {
      // Live session confirmed -- persist snapshot for future outages.
      saveLastKnownIdentity(session.user);
    } else if (!isNetworkError) {
      // Session is null and it's NOT a network error → confirmed
      // unauthenticated (401 or genuine sign-out).  Clear stored identity.
      clearLastKnownIdentity();
    }
    // When isNetworkError is true and session is null, we intentionally
    // do NOT clear the stored identity -- the outage may be transient.
  }, [isLoading, session?.user, isNetworkError]);

  // ---------- Resolve effective user & auth state ------------------------
  // Priority:
  //   1. Live session user (from TanStack Query cache or fresh fetch)
  //   2. Last-known localStorage identity (only during network errors)
  //   3. null (unauthenticated)

  const liveUser = session?.user ?? null;

  // Only read from localStorage when we have no live user AND we have a
  // network error (i.e. we can't confirm auth state).
  const lastKnown =
    !liveUser && isNetworkError && !isLoading
      ? getLastKnownIdentity()
      : null;

  // The user exposed to the rest of the app.
  // When falling back to lastKnown, provide `email: ""` to satisfy the
  // AuthContextValue['user'] type.  Email is intentionally not stored in
  // localStorage so it is unavailable in degraded mode.
  const effectiveUser: AuthContextValue["user"] = liveUser
    ? liveUser
    : lastKnown
      ? { id: lastKnown.id, name: lastKnown.name, email: "", image: lastKnown.image ?? null }
      : null;

  // ---------- Derived auth mode ------------------------------------------
  // "degraded" when the user identity comes from cache/localStorage and
  // the server is unreachable.  "live" otherwise.
  const authMode: "live" | "degraded" =
    effectiveUser && isNetworkError ? "degraded" : "live";

  // ---------- Telemetry: sync user.id with OTel span processor -----------
  // Uses a ref to avoid calling set/clear on every render when the ID
  // hasn't changed.  Only the opaque UUID is sent -- no PII.
  const prevUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const userId = effectiveUser?.id;
    if (userId && userId !== prevUserIdRef.current) {
      setTelemetryUserId(userId);
      prevUserIdRef.current = userId;
    } else if (!userId && prevUserIdRef.current) {
      clearTelemetryUserId();
      prevUserIdRef.current = undefined;
    }
  }, [effectiveUser?.id]);

  const signInWithMicrosoft = useCallback(async () => {
    if (!AUTH_MICROSOFT_ENABLED) {
      console.warn(
        "Microsoft auth is disabled. Set VITE_AUTH_MICROSOFT_ENABLED=true to enable."
      );
      return;
    }
    await authClient.signIn.social({
      provider: "microsoft",
      callbackURL: `${window.location.origin}/`,
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    clearTelemetryUserId();
    // Clear the persisted identity immediately so it doesn't linger.
    clearLastKnownIdentity();
    await authClient.signOut();
    // Clear the TanStack Query session cache so the user is immediately
    // treated as unauthenticated (no stale cached session lingers).
    queryClient.setQueryData(SESSION_QUERY_KEY, null);
    void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  const sendEmailOtpSignIn = useCallback(
    async (email: string): Promise<AuthFlowResult> => {
      try {
        await authClient.emailOtp.sendVerificationOtp({
          email,
          type: "sign-in",
        });
      } catch {
        // Swallow – always show a generic success to the user.
      }
      return { ok: true };
    },
    []
  );

  const signInWithEmailOtp = useCallback(
    async (email: string, otp: string): Promise<AuthFlowResult> => {
      try {
        const result = await authClient.signIn.emailOtp({
          email,
          otp,
        });

        if (typeof result === "object" && result !== null) {
          const r = result as {
            error?: { code?: unknown; message?: unknown } | null;
          };
          const code = r.error && typeof r.error.code === "string" ? r.error.code : undefined;
          const message =
            r.error && typeof r.error.message === "string" ? r.error.message : undefined;
          if (code || message) {
            return { ok: false, error: code ?? message ?? "UNKNOWN_ERROR" };
          }
        }

        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
        await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY, type: "all" });

        return { ok: true };
      } catch (error) {
        if (typeof error === "object" && error !== null) {
          const e = error as { code?: unknown; message?: unknown };
          if (typeof e.code === "string") {
            return { ok: false, error: e.code };
          }
          if (typeof e.message === "string") {
            return { ok: false, error: e.message };
          }
        }
        return { ok: false, error: "UNKNOWN_ERROR" };
      }
    },
    [queryClient]
  );

  // ---------- Invite claim -----------------------------------------------

  const claimInvite = useCallback(
    async (email: string, inviteCode: string): Promise<AuthFlowResult> => {
      try {
        const res = await fetch(`${API_URL}/api/invites/claim`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, inviteCode }),
        });

        if (!res.ok) {
          // Surface the machine-readable error code from the API response.
          try {
            const body = (await res.json()) as { error?: string };
            return { ok: false, error: body.error ?? "UNKNOWN_ERROR" };
          } catch {
            return { ok: false, error: "UNKNOWN_ERROR" };
          }
        }

        return { ok: true };
      } catch {
        // Network error – treat as failure so the UI can retry.
        return { ok: false, error: "NETWORK_ERROR" };
      }
    },
    []
  );

  // ---------- OTP register (new users via invite) -------------------------

  const sendMagicLinkRegister = useCallback(
    async (email: string, inviteCode: string): Promise<AuthFlowResult> => {
      // Step 1: claim the invite (validates code server-side)
      const claimResult = await claimInvite(email, inviteCode);

      // If the invite code is invalid, stop early – don't send an OTP.
      if (!claimResult.ok) {
        return claimResult;
      }

      // Step 2: trigger OTP delivery. Better Auth hook allows this because
      // the invite_claims row now exists.
      await sendEmailOtpSignIn(email);

      return { ok: true };
    },
    [claimInvite, sendEmailOtpSignIn]
  );

  // ---------- Context value ----------------------------------------------

  const value: AuthContextValue = {
    user: effectiveUser,
    isAuthenticated: !!effectiveUser,
    isLoading,
    authMode,
    authError: sessionError,
    signInWithMicrosoft,
    handleSignOut,
    sendEmailOtpSignIn,
    signInWithEmailOtp,
    claimInvite,
    sendMagicLinkRegister,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 *
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
